import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, query, where, getDocs, orderBy, limit 
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faSpinner, faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import styles from './Profile.module.css';

const VendorInventoryTab = ({ profileData, isMobile }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('audioEquipment');
  const [inventoryItems, setInventoryItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState(12);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [isSearching, setIsSearching] = useState(false);

  // Equipment categories
  const equipmentCategories = [
    { id: 'audioEquipment', label: 'Audio Equipment' },
    { id: 'backlineEquipment', label: 'Backline Equipment' },
    { id: 'lightingEquipment', label: 'Lighting Equipment' },
    { id: 'visualEquipment', label: 'Visual Equipment' },
    { id: 'pyroEquipment', label: 'Pyro Equipment' },
    { id: 'stagingEquipment', label: 'Staging Equipment' },
    { id: 'miscellaneousEquipment', label: 'Miscellaneous Equipment' }
  ];

  // Get category display name
  const getCategoryDisplayName = (categoryId) => {
    const categories = {
      'audioEquipment': 'Audio Equipment',
      'backlineEquipment': 'Backline Equipment',
      'lightingEquipment': 'Lighting Equipment',
      'visualEquipment': 'Visual Equipment',
      'pyroEquipment': 'Pyro Equipment',
      'stagingEquipment': 'Staging Equipment',
      'miscellaneousEquipment': 'Miscellaneous Equipment'
    };
    return categories[categoryId] || 'Equipment';
  };

  // Fetch inventory data
  const fetchInventory = useCallback(async (searchQuery = '') => {
    if (!profileData?.userId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Create base query
      let inventoryQuery;
      
      if (searchQuery) {
        // If searching, fetch all equipment and filter client-side
        inventoryQuery = query(
          collection(db, 'users', profileData.userId, 'inventoryGroups'),
          orderBy('updatedAt', 'desc'),
          limit(24)
        );
        
        setIsSearching(true);
      } else {
        // Otherwise filter by category
        inventoryQuery = query(
          collection(db, 'users', profileData.userId, 'inventoryGroups'),
          where('inventoryType', '==', getCategoryDisplayName(activeCategory)),
          orderBy('updatedAt', 'desc'),
          limit(displayedItems)
        );
        
        setIsSearching(false);
      }
      
      const querySnapshot = await getDocs(inventoryQuery);
      
      // Transform and filter the data
      let items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // If searching, do client-side filtering
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        items = items.filter(item => {
          // Match against name, brand, model, description, keywords
          return (
            item.name?.toLowerCase().includes(query) ||
            item.brand?.toLowerCase().includes(query) ||
            item.model?.toLowerCase().includes(query) ||
            item.description?.toLowerCase().includes(query) ||
            item.keywords?.some(keyword => keyword.includes(query))
          );
        });
        
        // Then filter by active category
        if (activeCategory) {
          items = items.filter(item => 
            item.inventoryType === getCategoryDisplayName(activeCategory)
          );
        }
      }
      
      setInventoryItems(items);
      setHasMoreItems(items.length >= displayedItems);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setIsLoading(false);
    }
  }, [profileData?.userId, activeCategory, displayedItems]);

  // Load more items
  const loadMoreItems = () => {
    setDisplayedItems(prev => prev + 12);
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setDisplayedItems(12); // Reset pagination when category changes
    setSearchTerm(''); // Clear search when category changes
  };

  // Handle search
  const handleSearch = () => {
    fetchInventory(searchTerm);
  };

  // Handle item click
 const handleItemClick = (item) => {
  // Extract inventory type from item and convert to route format
  const activeCategory = item.inventoryType.toLowerCase().replace(/\s+/g, '');
  navigate(`/equipment/${profileData.userId}/${activeCategory}/${item.id}`, {
    state: { 
      userName: profileData.userName,
      backPath: `/${profileData.userName}`
    }
  });
};

  // Initial fetch and category change effect
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory, activeCategory, displayedItems]);

  // Render inventory item card
  const renderInventoryCard = (item) => {
    return (
      <div 
        className={styles.inventoryCard} 
        key={item.id}
        onClick={() => handleItemClick(item)}
      >
        <div className={styles.inventoryCardImageContainer}>
          {item.coverPhotoUrl ? (
            <img 
              src={item.coverPhotoUrl} 
              alt={item.name} 
              className={styles.inventoryCardImage}
            />
          ) : (
            <div className={styles.defaultImage}>
              <span>{item.name?.charAt(0)}</span>
            </div>
          )}
        </div>
        
        <div className={styles.inventoryCardContent}>
          <h3 className={styles.inventoryCardTitle}>
            {item.name}
          </h3>
          
          <p className={styles.inventoryCardBrand}>
            {item.brand} {item.model && `- ${item.model}`}
          </p>
          
          {item.description && (
            <p className={styles.inventoryCardDescription}>{item.description}</p>
          )}
          
          <div className={styles.inventoryCardDetails}>
            <span>₹{item.rentPerDay}/day</span>
            <span>Qty: {item.totalQuantity}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.inventoryTabContainer}>
      {/* Search and Category Filter */}
      <div className={styles.inventorySearchContainer}>
        <div className={styles.InventorySearchBar}>
          <input 
            type="text" 
            placeholder="Search inventory..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className={styles.inventorySearchInput}
          />
          <button 
            className={styles.inventorySearchButton}
            onClick={handleSearch}
            disabled={isLoading}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className={styles.inventoryCategoryTabs}>
        {equipmentCategories.map(category => (
          <button
            key={category.id}
            className={`${styles.categoryTab} ${activeCategory === category.id ? styles.activeCategory : ''}`}
            onClick={() => handleCategoryChange(category.id)}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Inventory Grid */}
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
          <p>Loading inventory items...</p>
        </div>
      ) : inventoryItems.length === 0 ? (
        <div className={styles.emptyState}>
          <FontAwesomeIcon icon={faFileAlt} className={styles.emptyIcon} />
          {isSearching ? (
            <p>No items matching your search in this category.</p>
          ) : (
            <p>No items in this category yet.</p>
          )}
        </div>
      ) : (
        <>
          <div className={styles.inventoryGrid}>
            {inventoryItems.slice(0, displayedItems).map(item => renderInventoryCard(item))}
          </div>
          
          {hasMoreItems && (
            <div className={styles.showMoreContainer}>
              <button 
                className={styles.showMoreButton}
                onClick={loadMoreItems}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VendorInventoryTab;